// MessagingStreamingDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored (AdSense)
// Rewritten to match the same “template-first + Say + Panels + FB-like Home Feed examples” style
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* ignore */
      }
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* ignore */
    }
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <h3 className="h6 text-uppercase text-muted mb-0">Sponsored</h3>
          <span className="badge rounded-pill text-bg-light border text-muted">Ads</span>
        </div>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small helpers (same style as your updated pages) ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <h2 className="h4 mb-2 fw-bold">{title}</h2>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

const Say = ({ children }) => (
  <div className="alert alert-primary d-flex align-items-start gap-2 py-2 mb-3" role="note">
    <span className="badge text-bg-primary">Say</span>
    <div className="mb-0">{children}</div>
  </div>
);

const TagPill = ({ children }) => (
  <span className="badge rounded-pill text-bg-light border text-muted fw-semibold">{children}</span>
);

const Panel = ({ title, tag, children }) => (
  <div className="card border-0 shadow-sm mt-3">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div className="fw-semibold">{title}</div>
        {tag ? <TagPill>{tag}</TagPill> : null}
      </div>
      {children}
    </div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mb-0">
    {items.map((it, idx) => (
      <li key={idx} className={idx === items.length - 1 ? "" : "mb-3"}>
        <div>{it.main}</div>
        {it.detail ? <div className="text-muted mt-1">{it.detail}</div> : null}
      </li>
    ))}
  </ul>
);

const MonoBlock = ({ children }) => (
  <pre className="bg-light border rounded-3 p-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
    <code>{children}</code>
  </pre>
);

/* ================== TOC ================== */
const TOC = [
  { id: "queues-logs", label: "A) Queues vs Logs; semantics" },
  { id: "ordering", label: "B) Ordering, groups, backpressure" },
  { id: "idempotency", label: "C) Idempotency, dedupe, DLQs" },
  { id: "patterns", label: "D) Sagas, Outbox, CDC" },
  { id: "stream-batch", label: "E) Stream vs Batch; windows" },
  { id: "feed", label: "F) Facebook-like Home Feed example" },
  { id: "metrics", label: "G) Metrics & SLOs" },
  { id: "oneliners", label: "H) One-liners" },
];

/* ================== page ================== */
export default function MessagingStreamingDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveId(entry.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  // Running example numbers (Facebook-like Home Feed)
  const fb = useMemo(() => {
    const DAU = 200_000_000;
    const eventsPerFeedOpen = 10; // fanout writes (likes, impressions, rank logs, etc.) for illustration
    const feedOpensPerUserPerDay = 8;
    const feedOpensPerDay = DAU * feedOpensPerUserPerDay; // 1.6B
    const avgFeedOpenRps = feedOpensPerDay / 86400; // ~18.5k
    const peakMultiplier = 6;
    const peakFeedOpenRps = avgFeedOpenRps * peakMultiplier; // ~111k

    const interactionEventsPerDay = feedOpensPerDay * eventsPerFeedOpen; // illustrative
    const avgInteractionRps = interactionEventsPerDay / 86400;

    return {
      DAU,
      feedOpensPerUserPerDay,
      feedOpensPerDay,
      avgFeedOpenRps,
      peakMultiplier,
      peakFeedOpenRps,
      eventsPerFeedOpen,
      interactionEventsPerDay,
      avgInteractionRps,
    };
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Messaging &amp; streaming</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(13,110,253,.08), rgba(108,117,125,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1 fw-bold">6) Messaging &amp; streaming — detailed guide</h1>
          <p className="text-muted mb-0" style={{ maxWidth: 920, margin: "0 auto" }}>
            Queues vs logs, ordering, idempotency, DLQs, outbox/CDC, and stream processing —
            with a running example: <strong>Facebook-like Home Feed</strong>.
          </p>
        </div>
      </header>

      {/* layout */}
      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <h3 className="h6 text-uppercase text-muted mb-0">On this page</h3>
                  <TagPill>TOC</TagPill>
                </div>
                <nav className="nav flex-column small">
                  {TOC.map(({ id, label }) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className={`nav-link px-0 ${activeId === id ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === id ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">Tip: Use “Print” to save as PDF.</div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* A */}
          <Section id="queues-logs" title="A) Queues vs Logs — and delivery semantics">
            <Say>
              “Queues are for distributing work; logs/streams are for durable events with many consumers.
              We design for <strong>at-least-once</strong> and make consumers idempotent.”
            </Say>

            <Panel title="Queues (SQS/RabbitMQ/queue-mode PubSub)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Model:</strong> work items are consumed and removed; focus is “do this task”.
                      </>
                    ),
                    detail: "Great for background jobs, retries, worker pools, priorities.",
                  },
                  {
                    main: (
                      <>
                        <strong>Semantics:</strong> usually <em>at-least-once</em>; duplicates possible.
                      </>
                    ),
                    detail: "Use visibility timeouts + retry policies; DLQ for poison messages.",
                  },
                  {
                    main: (
                      <>
                        <strong>Ordering:</strong> best-effort; strict FIFO reduces throughput (single shard).
                      </>
                    ),
                    detail: "If you need strict order, scope it tightly (per entity).",
                  },
                ]}
              />
            </Panel>

            <Panel title="Logs / Streams (Kafka/Kinesis/Pulsar)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Model:</strong> append-only partitions; consumers track offsets; retention by time/size.
                      </>
                    ),
                    detail: "Great for event sourcing, audit, replay, multiple independent consumers.",
                  },
                  {
                    main: (
                      <>
                        <strong>Ordering:</strong> guaranteed only <strong>within a partition</strong>.
                      </>
                    ),
                    detail: "Never promise global ordering across partitions.",
                  },
                  {
                    main: (
                      <>
                        <strong>Guarantees:</strong> at-least-once by default; “exactly-once” is effectively-once via idempotency + atomic commits.
                      </>
                    ),
                    detail: "Kafka transactions can help, but downstream must still be idempotent.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Semantics cheat sheet" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>At-least-once:</strong> duplicates can happen → consumers must be idempotent.
                      </>
                    ),
                  },
                  {
                    main: (
                      <>
                        <strong>At-most-once:</strong> may drop messages → use only for non-critical signals.
                      </>
                    ),
                  },
                  {
                    main: (
                      <>
                        <strong>Effectively-once:</strong> idempotency + atomic commit of “processed offset” with side effect.
                      </>
                    ),
                    detail: "Typically implemented via unique constraints/UPSERTs, transactions, or outbox pattern.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* Optional in-article ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
            </div>
          )}

          {/* B */}
          <Section id="ordering" title="B) Ordering, partition keys, consumer groups, backpressure">
            <Say>
              “Ordering is <strong>per partition</strong>. We choose a key that matches the entity that needs order
              (like <code>post_id</code> or <code>user_id</code>), and we manage hot keys explicitly.”
            </Say>

            <Panel title="Partition keys & ordering" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Pick a key that needs ordering:</strong> <code>order_id</code>, <code>payment_id</code>,{" "}
                        <code>post_id</code>, <code>account_id</code>.
                      </>
                    ),
                    detail: "If you pick the wrong key, you’ll get out-of-order behavior where it matters.",
                  },
                  {
                    main: (
                      <>
                        <strong>Hot keys:</strong> “celebrity” users / viral posts create partition hotspots.
                      </>
                    ),
                    detail: "Mitigate with dedicated partitions, salting, or multi-stage fanout.",
                  },
                  {
                    main: (
                      <>
                        <strong>More partitions:</strong> more parallelism, but more overhead (rebalance, files, memory).
                      </>
                    ),
                    detail: "Keep partitions aligned to scale targets and operational capacity.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Consumer groups & scaling" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Rule:</strong> in a group, only one active consumer per partition.
                      </>
                    ),
                  },
                  {
                    main: (
                      <>
                        <strong>Scale:</strong> add partitions and consumers; handle rebalances with fast startup/state restore.
                      </>
                    ),
                    detail: "Commit offsets frequently, but not so often you add overhead—measure.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Backpressure & flow control" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Pull systems:</strong> Kafka lets consumers control rate (pause/resume).
                      </>
                    ),
                    detail: "Prefer bounded in-flight buffers to avoid OOM and long GC pauses.",
                  },
                  {
                    main: (
                      <>
                        <strong>Levers:</strong> prefetch limits, max in-flight per partition, batch sizes, max.poll.records.
                      </>
                    ),
                    detail: "Tune to keep latency stable and avoid long processing stalls that cause rebalances.",
                  },
                  {
                    main: (
                      <>
                        <strong>Autoscale signal:</strong> consumer lag + processing time (not CPU alone).
                      </>
                    ),
                    detail: "Lag growth is the best early indicator you’re falling behind.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* C */}
          <Section id="idempotency" title="C) Idempotency, dedupe, retries, poison messages, DLQs">
            <Say>
              “We assume duplicates and make side effects idempotent using unique keys/UPSERTs. Retries have a cap,
              and poison messages go to DLQ with a safe replay path.”
            </Say>

            <Panel title="Idempotency keys (the practical way)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Use a stable key:</strong> natural ID (<code>payment_id</code>) or client-supplied{" "}
                        <code>idempotency_key</code>.
                      </>
                    ),
                  },
                  {
                    main: (
                      <>
                        <strong>Protect side effects:</strong> DB unique constraint + UPSERT, or a dedupe table keyed by the idempotency key.
                      </>
                    ),
                    detail: "Return the previous result if the request is duplicated.",
                  },
                  {
                    main: (
                      <>
                        <strong>TTL for dedupe records:</strong> keep long enough to cover retry/replay windows.
                      </>
                    ),
                    detail: "Example: 24h–7d depending on business and retention needs.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Retries & DLQ" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Retry:</strong> exponential backoff + jitter; cap attempts.
                      </>
                    ),
                    detail: "Prevent retry storms from taking down dependencies.",
                  },
                  {
                    main: (
                      <>
                        <strong>DLQ:</strong> after N tries, move the message with error snapshot + headers.
                      </>
                    ),
                    detail: "DLQ must be searchable and have a safe replay tool (with throttling).",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* D */}
          <Section id="patterns" title="D) Patterns: Sagas, Outbox, CDC">
            <Say>
              “Outbox makes DB writes and events atomic. Sagas handle cross-service workflows with compensations.
              CDC is great when you can’t change the producer code.”
            </Say>

            <Panel title="Sagas (async workflows)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Choreography:</strong> services react to events (simple, but implicit).
                      </>
                    ),
                    detail: "Harder to see the whole workflow; duplicates/timeouts need careful handling.",
                  },
                  {
                    main: (
                      <>
                        <strong>Orchestration:</strong> a workflow engine drives steps and compensations (explicit, observable).
                      </>
                    ),
                    detail: "Temporal/Step Functions: clear state machine, retries, timeouts, audits.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Transactional Outbox" tag="Template">
              <MonoBlock>{`In one DB transaction:
  1) write business row (e.g., post/comment)
  2) write outbox row (event payload + outbox_id)

A relay publishes:
  - message key = outbox_id (or entity_id for ordering)
  - marks outbox row as sent (idempotent)
`}</MonoBlock>
            </Panel>

            <Panel title="CDC (Change Data Capture)" tag="Template">
              <BulletList
                items={[
                  { main: "Stream DB WAL/binlog with Debezium/DMS; consumers build search indexes, caches, analytics.", detail: "Best for legacy producers you can’t modify." },
                  { main: "Watch schema evolution, ordering, and PII filtering in the stream.", detail: "Use schema registry and field-level policies." },
                ]}
              />
            </Panel>
          </Section>

          {/* E */}
          <Section id="stream-batch" title="E) Stream vs Batch — windows, watermarks, late data">
            <Say>
              “Streaming is for freshness; batch is for completeness/backfills. We aggregate on event-time, define
              allowed lateness, and use watermarks to control when results finalize.”
            </Say>

            <Panel title="Stream processing" tag="Template">
              <BulletList
                items={[
                  { main: "Continuous low-latency pipelines (Flink/Kafka Streams/Beam/Spark SS).", detail: "Stateful, checkpointed, replayable." },
                  { main: "Use for real-time metrics, fraud signals, materialized views, near-real-time ETL.", detail: "Be explicit about state size and checkpoint cost." },
                ]}
              />
            </Panel>

            <Panel title="Batch processing" tag="Template">
              <BulletList
                items={[
                  { main: "Periodic jobs over large datasets.", detail: "Great for heavy joins, recomputations, backfills, re-indexing." },
                ]}
              />
            </Panel>

            <Panel title="Windows & time domains" tag="Template">
              <BulletList
                items={[
                  { main: <><strong>Processing vs event-time:</strong> event-time handles late events correctly.</>, detail: "Feeds and mobile clients produce late events frequently." },
                  { main: <><strong>Windows:</strong> tumbling, sliding, session.</>, detail: "Choose based on how users behave (sessions) vs fixed intervals (tumbling)." },
                  { main: <><strong>Watermarks:</strong> define how long you wait for late data.</>, detail: "Allowed lateness is a latency vs correctness trade-off." },
                ]}
              />
            </Panel>
          </Section>

          {/* F */}
          <Section id="feed" title="F) Facebook-like Home Feed — messaging & streaming design">
            <Panel title="What events exist in a Home Feed system?" tag="Example">
              <BulletList
                items={[
                  { main: <><strong>Write-side:</strong> <code>PostCreated</code>, <code>PostUpdated</code>, <code>FollowCreated</code>, <code>BlockUpdated</code></>, detail: "These events drive fanout, indexing, and cache invalidation." },
                  { main: <><strong>Engagement:</strong> <code>LikeCreated</code>, <code>CommentCreated</code>, <code>Impression</code></>, detail: "Used for ranking features, counters, notifications, and analytics." },
                  { main: <><strong>Read-side signals:</strong> <code>FeedOpened</code>, <code>FeedItemViewed</code></>, detail: "Power experimentation, observability, and ranking training data." },
                ]}
              />
            </Panel>

            <Panel title="Queue vs stream in the Home Feed" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Queues:</strong> background work like media processing, notification sends, search re-index retries.
                      </>
                    ),
                    detail: "Work distribution, retries, DLQ — tasks are “do once (idempotent)”.",
                  },
                  {
                    main: (
                      <>
                        <strong>Streams:</strong> durable event log for fanout, ranking features, audit, replay.
                      </>
                    ),
                    detail: "Multiple consumers (ranking, notifications, analytics, search) read independently.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Partitioning & ordering choices" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Ordering requirement:</strong> per <code>post_id</code> (update/delete must follow create).
                      </>
                    ),
                    detail: "Key topic by post_id for post lifecycle; key engagement by post_id or viewer_id depending on consumer needs.",
                  },
                  {
                    main: (
                      <>
                        <strong>Hot key:</strong> viral post becomes a hotspot.
                      </>
                    ),
                    detail: "Use multi-stage fanout (partition by post_id but scale with subtopics/shards for engagement).",
                  },
                ]}
              />
            </Panel>

            <Panel title="Backpressure reality check (numbers)" tag="Example">
              <MonoBlock>{`Assume:
  DAU: ${fb.DAU.toLocaleString()}
  Feed opens/day: ${fb.feedOpensPerDay.toLocaleString()}  (~${Math.round(fb.avgFeedOpenRps).toLocaleString()} avg RPS)
  Peak multiplier: ${fb.peakMultiplier}x  => ~${Math.round(fb.peakFeedOpenRps).toLocaleString()} peak RPS

If each feed-open produces ~${fb.eventsPerFeedOpen} events (signals/metrics):
  Events/day: ${fb.interactionEventsPerDay.toLocaleString()}
  Avg events RPS: ~${Math.round(fb.avgInteractionRps).toLocaleString()} RPS

Design implications:
  - partition enough for peak
  - consumers autoscale on lag
  - bounded buffers + pause/resume
`}</MonoBlock>
            </Panel>

            <Panel title="Idempotency & DLQ in Home Feed" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Idempotency:</strong> use <code>event_id</code> or <code>outbox_id</code>; sinks do UPSERTs.
                      </>
                    ),
                    detail: "Duplicates are expected in at-least-once pipelines.",
                  },
                  {
                    main: (
                      <>
                        <strong>Poison messages:</strong> schema mismatch / corrupt payload → DLQ with replay tooling.
                      </>
                    ),
                    detail: "Replays must be throttled to avoid re-triggering an incident.",
                  },
                  {
                    main: (
                      <>
                        <strong>Privacy:</strong> filter/redact PII in streams; enforce access controls at read time as a safety net.
                      </>
                    ),
                    detail: "Treat event streams as production data with governance.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* G */}
          <Section id="metrics" title="G) Metrics & SLOs to mention in interviews">
            <Say>
              “I track lag per partition and end-to-end latency, plus DLQ rate. Lag growth is the best early warning
              that we’re falling behind.”
            </Say>

            <Panel title="What to measure" tag="Template">
              <BulletList
                items={[
                  { main: <><strong>Producer:</strong> publish latency, retries, batch sizes, acks.</>, detail: "Spikes often signal broker health or network issues." },
                  { main: <><strong>Broker:</strong> partition skew, leader load, disk usage, ISR health.</>, detail: "Skew causes one partition to melt while others idle." },
                  { main: <><strong>Consumer:</strong> lag by partition, processing time, error/retry/DLQ rates, checkpoint age.</>, detail: "Lag SLOs should be per topic and per consumer group." },
                  { main: <><strong>Pipeline:</strong> end-to-end latency, % late events, watermark delay.</>, detail: "Streaming correctness depends on event-time and late data handling." },
                ]}
              />
            </Panel>

            <Panel title="Example SLOs" tag="Template">
              <BulletList
                items={[
                  { main: "“99% of events processed within 5s of arrival.”" },
                  { main: "“DLQ < 0.1% over 24 hours.”" },
                  { main: "“Consumer lag per partition stays under 30s at p99.”" },
                ]}
              />
            </Panel>
          </Section>

          {/* H */}
          <Section id="oneliners" title="H) One-liners for the interview">
            <Panel title="Say these confidently" tag="Template">
              <BulletList
                items={[
                  { main: "“Queues distribute work; streams are durable logs for many consumers.”" },
                  { main: "“Ordering is per partition; we key by the entity that needs order and handle hot keys explicitly.”" },
                  { main: "“We design for at-least-once and make consumers idempotent; effectively-once comes from idempotency + atomic commits.”" },
                  { main: "“Outbox keeps DB and broker consistent; CDC feeds search/analytics without app changes.”" },
                  { main: "“Lag and end-to-end latency are the two metrics I watch first.”" },
                ]}
              />
            </Panel>
          </Section>

          {/* Optional bottom ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
            </div>
          )}
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
